// @ts-nocheck
// Form Fields pane — list-first field manager (Airtable/Google-Forms pattern):
// the field LIST is the centerpiece; creating and editing happen in
// FieldEditorModal; the phone-frame BrowserPreview is a read-only live mirror.
// A segmented toggle with per-context accents + a context banner make it
// unmistakable whether the Player or Team form is being edited. Save persists
// BOTH lists together (they're one form).
import { useMemo, useState } from 'react';
import _ from 'lodash';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  User, Users, Plus, Pencil, X, GripVertical, Lock, Eye,
} from 'lucide-react';
import { isIndividualSport } from '@staty/shared/utils/sportCategory';
import BrowserPreview from '@components/BrowserPreview';
import FieldEditorModal, { FIELD_TYPE_OPTIONS } from '@/components/FieldEditorModal';
import ensureEmailField from '@/utils/ensureEmailField';
import SectionCard from './SectionCard';

const TYPE_META = {
  ...Object.fromEntries(FIELD_TYPE_OPTIONS.map((o) => [o.value, o])),
  // Legacy stored shape: older registrations saved fieldType 'textbox'.
  textbox: { value: 'text', label: 'Text', icon: '📝' },
};

const CONTEXTS = {
  player: {
    draftKey: 'fields',
    icon: User,
    accentText: 'text-blue-700',
    accentBanner: 'border-l-4 border-blue-500 bg-blue-50',
    accentButton: 'bg-blue-600 hover:bg-blue-700',
    // Feeds the global [data-slot=tabs-trigger][data-state=active] rule, which
    // paints the active pill with hsl(var(--primary)).
    accentPrimaryVar: '221.2 83.2% 53.3%', // tailwind blue-600
    bannerTitle: 'Player form',
    // Truthful "who fills this": ONE submitter enters the whole roster in
    // every mode — playerPaySeparately teammates only pay via links, and
    // First/Last are ordinary (locked) entries in this very list, not
    // "collected automatically" (owner-corrected 2026-09-29).
    bannerHint: (sport) => (sport && isIndividualSport(sport)
      ? 'Filled out by the person registering: their own info can auto-fill from their profile, and they enter their partner. An Email field is added automatically when players pay individually or must sign a waiver.'
      : 'Filled out by the person registering: they enter each player on the roster themselves (First and Last Name are built in below). An Email field is added automatically when players pay individually or must sign a waiver.'),
    audienceLabel: 'Players',
    previewUrl: 'yourleague.com/register/players',
  },
  team: {
    draftKey: 'teamFields',
    icon: Users,
    accentText: 'text-violet-700',
    accentBanner: 'border-l-4 border-violet-500 bg-violet-50',
    accentButton: 'bg-violet-600 hover:bg-violet-700',
    accentPrimaryVar: '262.1 83.3% 57.8%', // tailwind violet-600
    bannerTitle: 'Team form',
    bannerHint: () => 'Filled out once by the person registering, before the player list. Team Name (controlled by the toggle below) and Division are built in; your custom questions appear after them.',
    audienceLabel: 'Teams',
    previewUrl: 'yourleague.com/register/team',
  },
};

function FieldRow({ field, dndId, sortable, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dndId,
    disabled: !sortable,
  });
  const typeMeta = TYPE_META[field.fieldType] || { icon: '📝', label: field.fieldType || 'Text' };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-lg border bg-card px-3 py-2.5 ${
        isDragging ? 'z-10 shadow-lg' : ''
      }`}
    >
      {sortable ? (
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground/60 hover:text-muted-foreground"
          aria-label={`Reorder ${field.name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      ) : (
        <Lock className="h-4 w-4 text-muted-foreground/50" title="Built-in field" />
      )}

      <button
        type="button"
        onClick={onEdit}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
        title={`Edit ${field.name}`}
      >
        <span className="truncate text-sm font-medium">{field.name}</span>
        <Badge variant="outline" className="shrink-0 gap-1 font-normal text-muted-foreground">
          <span>{typeMeta.icon}</span> {typeMeta.label}
        </Badge>
        {field.required && (
          <Badge variant="secondary" className="shrink-0 font-normal">Required</Badge>
        )}
      </button>

      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Edit ${field.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        {!field.permanent && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            aria-label={`Delete ${field.name}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function FormFieldsSection({ d, sport = '', registrationName = '' }) {
  const { draft, setField, dirtyBySection, savingSection, saveSection } = d;
  const [activeTab, setActiveTab] = useState('player');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null → create

  const ctx = CONTEXTS[activeTab];
  const fields = draft[ctx.draftKey] || [];

  // Mirror BrowserPreview's partition: pinned fields stay at the top, immovable.
  const pinnedFields = useMemo(() => fields.filter((f) => f.pinned), [fields]);
  const movableFields = useMemo(() => fields.filter((f) => !f.pinned), [fields]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const setList = (next) => setField(ctx.draftKey, next);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = movableFields.findIndex((f, i) => `mov-${i}` === active.id);
    const to = movableFields.findIndex((f, i) => `mov-${i}` === over.id);
    if (from === -1 || to === -1) return;
    setList([...pinnedFields, ...arrayMove(movableFields, from, to)]);
  };

  const indexInDraft = (field) => fields.indexOf(field);

  const onSaveField = (built) => {
    if (editingIndex === null) {
      setList([...fields, built]);
    } else {
      const next = [...fields];
      next[editingIndex] = built;
      setList(next);
    }
  };

  const onDeleteField = (field) => {
    const next = [...fields];
    next.splice(indexInDraft(field), 1);
    setList(next);
  };

  const openCreate = () => { setEditingIndex(null); setModalOpen(true); };
  const openEdit = (field) => { setEditingIndex(indexInDraft(field)); setModalOpen(true); };

  const BannerIcon = ctx.icon;

  return (
    <SectionCard
      id="formFields"
      title="Form Fields"
      description="What the person registering fills out. One form covers the team details and every player on the roster. Saving stores both tabs together."
      dirty={dirtyBySection.formFields}
      saving={savingSection === 'formFields'}
      onSave={() => saveSection('formFields')}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full max-w-md grid-cols-2 p-1">
          {Object.entries(CONTEXTS).map(([key, c]) => {
            const Icon = c.icon;
            const count = (draft[c.draftKey] || []).length;
            return (
              <TabsTrigger
                key={key}
                value={key}
                className="gap-2 py-2"
                style={{ '--primary': c.accentPrimaryVar }}
              >
                <Icon className="h-4 w-4" />
                {c.bannerTitle} ({count})
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className={`flex items-start gap-3 rounded-r-lg p-3 ${ctx.accentBanner}`}>
        <BannerIcon className={`mt-0.5 h-5 w-5 shrink-0 ${ctx.accentText}`} />
        <div className="text-sm">
          <span className={`font-semibold ${ctx.accentText}`}>{ctx.bannerTitle}</span>
          <span className="text-muted-foreground">: {ctx.bannerHint(sport)}</span>
        </div>
      </div>

      {activeTab === 'team' && (
        <div className="flex items-start justify-between gap-4 rounded-lg border px-4 py-3">
          <div>
            <Label htmlFor="requireTeamName" className="text-sm font-medium">
              Require team name
            </Label>
            <p className="mt-1 text-xs text-muted-foreground">
              When off, teams sign up without entering a name. One is generated
              automatically and the roster&apos;s player names are shown instead
              (common for doubles and individual formats). A division with its
              own setting overrides this.
            </p>
          </div>
          {/* Stored polarity stays `excludeTeamName` (ADR 0049); the UI shows
              the positive "require" the owner asked for. Seeded RESOLVED in
              useRegistrationDraft — individual sports default OFF — and saved
              with no-stamping (explicit only when ≠ the sport default). */}
          <Switch
            id="requireTeamName"
            checked={!draft.excludeTeamName}
            onCheckedChange={(v) => setField('excludeTeamName', !v)}
          />
        </div>
      )}

      <div className="space-y-2">
        {fields.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No fields yet. Add your first question below.
          </div>
        ) : (
          <>
            {pinnedFields.map((field) => (
              <FieldRow
                key={`pin-${field.fieldId}`}
                field={field}
                dndId={`pin-${field.fieldId}`}
                sortable={false}
                onEdit={() => openEdit(field)}
                onDelete={() => onDeleteField(field)}
              />
            ))}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext
                items={movableFields.map((f, i) => `mov-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                {movableFields.map((field, i) => (
                  <FieldRow
                    key={`mov-${field.fieldId}-${i}`}
                    field={field}
                    dndId={`mov-${i}`}
                    sortable
                    onEdit={() => openEdit(field)}
                    onDelete={() => onDeleteField(field)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}
        <Button onClick={openCreate} className={`w-full text-white ${ctx.accentButton}`}>
          <Plus className="mr-2 h-4 w-4" /> Add field
        </Button>
      </div>

      <div className="space-y-2 pt-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Eye className="h-3.5 w-3.5" /> Live preview
        </div>
        {/* Read-only replica of the PUBLIC register funnel (variant="register").
            The player tab mirrors the save-time Email injection display-time
            via ensureEmailField; the team tab renders the built-in Team Name
            (reactive to the toggle above) + Division ahead of the customs. */}
        <BrowserPreview
          fields={activeTab === 'player' ? ensureEmailField(fields, d.registration) : fields}
          url={ctx.previewUrl}
          emptyMessage="No fields configured yet. Add fields above to see them here."
          type={activeTab}
          variant="register"
          register={{
            sport,
            registrationName,
            excludeTeamName: !!draft.excludeTeamName,
            minPlayers: draft.requirePlayers ? Number(draft.minPlayers) || 1 : 1,
            maxPlayers: draft.requirePlayers ? Number(draft.maxPlayers) || 0 : 0,
          }}
        />
        {activeTab === 'team' && (
          <p className="text-xs text-muted-foreground">
            The real form also asks for Coach / Team Manager details and SMS
            consent, based on your Team &amp; Roster settings (not shown here).
          </p>
        )}
      </div>

      <FieldEditorModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        field={editingIndex === null ? null : fields[editingIndex]}
        existingFieldIds={fields.map((f) => f.fieldId)}
        audienceLabel={ctx.audienceLabel}
        onSave={onSaveField}
      />
    </SectionCard>
  );
}
